import { todayIn } from './dates.js';
/**
 * How big a task should look, and how loudly it should say so.
 *
 * The brief was two-sided and the two sides pull against each other: nothing
 * important may be missed, and nothing may be alarming without cause. A flat
 * weighted sum fails both at once — it lets a P1 with no date shrink to the
 * size of a nagging P3, and it lets a trivial job with a date on it shout.
 *
 * So importance sets the size and everything else only escalates it. A P1 is
 * never small however relaxed it is; a P3 grows when it is genuinely pressing
 * and stays a pebble when it is not. Alarm is a separate reading (`heat`)
 * carried by the aura rather than the size, because "big" and "on fire" are
 * different facts and a rock should be able to be one without the other.
 */
const BASE = { p1: 1, p2: 0.7, p3: 0.45 };
const URGENCY = { none: 1, soon: 1.2, urgent: 1.45, critical: 1.45 };
/** The largest any of this can multiply to: 1 × 1.45 × 1.5. */
const CEILING = 1 * 1.45 * 1.5;
/** Days until the due date. Negative is overdue; null when there is no date. */
export function daysUntilDue(task, timeZone = 'Europe/London', now = new Date()) {
    if (!task.targetDate)
        return null;
    const today = todayIn(timeZone, now);
    const start = Date.UTC(today.year, today.month - 1, today.day);
    const [y, m, d] = task.targetDate.split('-').map(Number);
    return Math.round((Date.UTC(y, m - 1, d) - start) / 86_400_000);
}
function dueFactor(days) {
    if (days === null)
        return 1;
    if (days < 0)
        return 1.5;
    if (days <= 2)
        return 1.3;
    if (days <= 7)
        return 1.15;
    return 1;
}
export function taskWeight(task, timeZone = 'Europe/London', now = new Date()) {
    const days = daysUntilDue(task, timeZone, now);
    const rating = (BASE[task.importance] * URGENCY[task.urgency] * dueFactor(days)) / CEILING;
    // Heat is pressure, not worth: a P3 due today is hotter than a P1 with no
    // date, which is exactly the thing that must not be missed on a given day
    // even though the P1 is the bigger rock.
    const pressure = (days === null ? 0 : days < 0 ? 1 : days <= 1 ? 0.8 : days <= 3 ? 0.55 : days <= 7 ? 0.3 : 0.1) *
        0.75 +
        (task.urgency === 'none' ? 0 : task.urgency === 'soon' ? 0.3 : 1) * 0.25;
    return {
        rating,
        // Importance lifts the floor a little, so a forgotten P1 never sits
        // completely cold, but it cannot make a calm task look acute on its own.
        heat: Math.min(1, pressure + (task.importance === 'p1' ? 0.12 : task.importance === 'p2' ? 0.05 : 0)),
        dueInDays: days,
        overdue: days !== null && days < 0,
    };
}
/** dd M YYYY, or the words for having no date at all. */
export function dueLabel(task) {
    if (!task.targetDate)
        return 'No due date';
    const [y, m, d] = task.targetDate.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d).padStart(2, '0')} ${months[m - 1]} ${y}`;
}
//# sourceMappingURL=taskWeight.js.map